import { useState } from 'react'
import type {
  ProgramApplicationDetail,
  ProgramApplicationStatus,
} from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { usePermission } from '../../hooks/usePermission'

interface Props {
  application: ProgramApplicationDetail
  onClose: () => void
  onTransition: (body: {
    status?: ProgramApplicationStatus
    reviewer_notes?: string
  }) => Promise<void>
}

type Action = 'approve' | 'reject' | 'mark_paid' | null

function payloadString(payload: Record<string, unknown>, key: string): string {
  const v = payload[key]
  return typeof v === 'string' ? v : ''
}

function formatCurrencyCents(cents: unknown): string {
  if (typeof cents !== 'number') return '—'
  return `$${(cents / 100).toFixed(2)}`
}

function Field({ label, value, mono = false }: { label: string; value: string | null; mono?: boolean }) {
  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
      <p className={`mt-0.5 text-gray-900 dark:text-gray-100 ${mono ? 'font-mono text-xs' : ''}`}>
        {value || '—'}
      </p>
    </div>
  )
}

export function ProgramApplicationDetailModal({ application, onClose, onTransition }: Props) {
  const [action, setAction] = useState<Action>(null)
  const [notes, setNotes] = useState(application.reviewer_notes ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canWrite = usePermission('programs', 'write')
  const isPending = application.status === 'pending'
  const isApproved = application.status === 'approved'

  const submit = async (status: ProgramApplicationStatus) => {
    setLoading(true)
    setError(null)
    try {
      await onTransition({ status, reviewer_notes: notes.trim() || undefined })
      setAction(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed')
    } finally {
      setLoading(false)
    }
  }

  const saveNotesOnly = async () => {
    setLoading(true)
    setError(null)
    try {
      await onTransition({ reviewer_notes: notes.trim() })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save notes')
    } finally {
      setLoading(false)
    }
  }

  const eventDate = payloadString(application.payload, 'event_date')
  const eventName = payloadString(application.payload, 'event_name')
  const eventCity = payloadString(application.payload, 'event_city')
  const eventState = payloadString(application.payload, 'event_state')
  const eventType = payloadString(application.payload, 'event_type')
  const eventUrl = payloadString(application.payload, 'event_url')
  const applicantNotes = payloadString(application.payload, 'notes')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">
              {application.program === 'booth_fund' ? 'Booth Fund application' : application.program}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{application.status}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4 text-sm">
          <Field label="Applicant" value={application.applicant_name} />
          <Field label="Email" value={application.applicant_email} />
          <Field label="Submitted" value={new Date(application.created_at).toLocaleString()} />
          <Field
            label="Reviewed"
            value={application.reviewed_at ? new Date(application.reviewed_at).toLocaleString() : null}
          />
          <Field label="Shop" value={application.shop_handle ? `@${application.shop_handle}` : null} />
          <Field
            label="Has listing"
            value={application.shop_has_listing === null ? null : application.shop_has_listing ? 'yes' : 'no'}
          />
        </div>

        <div className="mb-6 rounded-xl border border-[#ecdefc] dark:border-[#3d2c4f] p-4">
          <h3 className="mb-3 font-medium text-gray-900 dark:text-gray-100">Event</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Name" value={eventName} />
            <Field label="Type" value={eventType.replace('_', ' ')} />
            <Field label="Date" value={eventDate} />
            <Field
              label="Location"
              value={[eventCity, eventState].filter(Boolean).join(', ') || null}
            />
            <Field label="Table fee" value={formatCurrencyCents(application.payload.table_fee_cents)} />
            <Field label="Website" value={eventUrl} />
          </div>
          {applicantNotes && (
            <div className="mt-4 rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-3 text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap">
              {applicantNotes}
            </div>
          )}
        </div>

        {application.utm && Object.keys(application.utm).length > 0 && (
          <div className="mb-6 rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-3 text-xs text-gray-600 dark:text-gray-300">
            <span className="font-medium">UTM</span>:{' '}
            {Object.entries(application.utm)
              .map(([k, v]) => `${k}=${v}`)
              .join(' · ')}
          </div>
        )}

        {canWrite && (
          <div className="mb-4">
            <label className="text-xs uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Reviewer notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="mt-1 w-full rounded-xl border border-gray-300 dark:border-[#3d2c4f] bg-white dark:bg-[#0f0d12] px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
              placeholder="Internal notes for ops"
            />
          </div>
        )}

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 dark:bg-red-900/30 p-3 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        {action === 'approve' && (
          <ConfirmBox
            title="Approve application"
            note="Marks the application as approved. Send the funds via Stripe/PayPal manually, then mark Paid."
            onConfirm={() => submit('approved')}
            onCancel={() => setAction(null)}
            loading={loading}
            variant="success"
          />
        )}
        {action === 'reject' && (
          <ConfirmBox
            title="Reject application"
            note="Saves the rejection. Notes are visible only to admins."
            onConfirm={() => submit('rejected')}
            onCancel={() => setAction(null)}
            loading={loading}
            variant="danger"
          />
        )}
        {action === 'mark_paid' && (
          <ConfirmBox
            title="Mark as paid"
            note="Confirm the funds have been transferred."
            onConfirm={() => submit('paid')}
            onCancel={() => setAction(null)}
            loading={loading}
            variant="success"
          />
        )}

        {!action && canWrite && (
          <div className="flex flex-wrap gap-2">
            {isPending && (
              <>
                <CustomButton onClick={() => setAction('approve')} variant="success">
                  Approve
                </CustomButton>
                <CustomButton onClick={() => setAction('reject')} variant="danger">
                  Reject
                </CustomButton>
              </>
            )}
            {isApproved && (
              <CustomButton onClick={() => setAction('mark_paid')} variant="success">
                Mark paid
              </CustomButton>
            )}
            <CustomButton onClick={saveNotesOnly} disabled={loading} variant="secondary">
              Save notes
            </CustomButton>
            <CustomButton onClick={onClose} variant="ghost">
              Close
            </CustomButton>
          </div>
        )}

        {!canWrite && (
          <div className="rounded-xl bg-gray-50 dark:bg-[#2a1f35] p-3 text-sm text-gray-600 dark:text-gray-300">
            Read-only — `programs:write` permission required to act on this application.
          </div>
        )}
      </div>
    </div>
  )
}

function ConfirmBox({
  title,
  note,
  onConfirm,
  onCancel,
  loading,
  variant,
}: {
  title: string
  note: string
  onConfirm: () => void
  onCancel: () => void
  loading: boolean
  variant: 'success' | 'danger'
}) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-[#3d2c4f] p-4">
      <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">{title}</h4>
      <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">{note}</p>
      <div className="flex gap-2">
        <CustomButton onClick={onConfirm} disabled={loading} variant={variant}>
          {loading ? 'Working...' : 'Confirm'}
        </CustomButton>
        <CustomButton onClick={onCancel} variant="secondary">
          Cancel
        </CustomButton>
      </div>
    </div>
  )
}
